import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import GoogleSignInButton from "@/components/shared/GoogleSignInButton";

const Login = () => {
  const [mode, setMode] = useState("google"); // "google" | "email"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, devLogin } = useAuth();
  const navigate = useNavigate();

  // Dev-only sign-in (no real Google OAuth). Backend must have DEV_LOGIN_ENABLED=true.
  const isDev = process.env.NODE_ENV === "development";
  const [devEmail, setDevEmail] = useState("demo.payee@payd.local");
  const doDevLogin = async () => {
    setLoading(true);
    try {
      await devLogin(devEmail);
      toast.success("Signed in (dev)");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Dev login failed (is DEV_LOGIN_ENABLED set?)");
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground">
        <Link to="/" className="flex items-center gap-2 text-primary-foreground">
          <div className="w-8 h-8 rounded-lg bg-white grid place-items-center text-primary font-bold">P</div>
          <span className="font-display text-xl font-bold">PAYD</span>
        </Link>
        <div>
          <h2 className="text-4xl font-bold tracking-tight leading-tight">Milestone escrow for modern work.</h2>
          <p className="mt-4 opacity-90">Fund. Approve. Release. A clean, trust-first way to pay freelancers.</p>
        </div>
        <div className="text-xs uppercase tracking-[0.2em] opacity-70">© PAYD {new Date().getFullYear()}</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-10">
            <div className="w-8 h-8 rounded-lg bg-primary grid place-items-center text-primary-foreground font-bold">P</div>
            <span className="font-display text-xl font-bold">PAYD</span>
          </Link>
          <h1 className="text-3xl font-bold tracking-tight">Welcome back</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sign in to your PAYD account.</p>

          <div className="mt-8 space-y-3">
            <GoogleSignInButton />
          </div>

          <div className="my-6 flex items-center gap-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {mode === "google" ? (
            <Button variant="ghost" onClick={() => setMode("email")} className="w-full" data-testid="use-email-btn">
              Use email instead
            </Button>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" data-testid="login-email-input" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" data-testid="login-password-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11" data-testid="login-submit-btn">
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          )}

          {isDev && (
            <div className="mt-8 rounded-lg border border-dashed border-border p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Dev login</div>
              <p className="text-xs text-muted-foreground mb-3">
                Sign in without Google (local only). Upserts the user on the backend.
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={devEmail}
                  onChange={(e) => setDevEmail(e.target.value)}
                  placeholder="email@payd.local"
                  className="h-11 flex-1 min-w-0"
                  data-testid="dev-login-email-input"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={doDevLogin}
                  disabled={loading || !devEmail}
                  className="h-11 whitespace-nowrap"
                  data-testid="dev-login-btn"
                >
                  Dev sign in
                </Button>
              </div>
            </div>
          )}

          <div className="mt-8 text-center text-sm text-muted-foreground">
            New to PAYD? <Link to="/register" className="text-primary font-medium hover:underline" data-testid="link-register">Create an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
